import { useState, useEffect, useRef } from "react";
import { Transition } from "@headlessui/react";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/20/solid";

function MultiSelect({
  label,
  options,
  onUpdate,
  onClear = false,
  selected,
  isLoading,
  optionStatus,
  fullWidth = false,
  labelPosition = "inset",
  singleSelect = false, // new prop for single select mode
  disabled = false, // new prop for disabled state
  // Form-field mode: the menu spans the trigger's width and the trigger lines
  // up with its label. Off by default so filter bars keep the compact menu.
  matchTriggerWidth = false,
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState("left-0"); // Track menu position
  const [menuPlacement, setMenuPlacement] = useState("bottom"); // Track vertical placement
  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  // Single select with a long list (e.g. times): open on the current choice.
  useEffect(() => {
    if (!isDropdownOpen || !singleSelect || !listRef.current) return;
    const current = listRef.current.querySelector('[data-selected="true"]');
    if (current) {
      listRef.current.scrollTop =
        current.offsetTop -
        listRef.current.clientHeight / 2 +
        current.offsetHeight / 2;
    }
  }, [isDropdownOpen, singleSelect]);

  const toggleDropdown = () => {
    if (disabled) return;

    if (!isDropdownOpen) {
      // Calculate position when opening
      const buttonRect = dropdownRef.current?.getBoundingClientRect();
      const menuWidth = 224; // w-56 = 14rem = 224px
      const menuHeight = 240; // Approximate max height of dropdown
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      if (buttonRect) {
        const spaceOnRight = windowWidth - buttonRect.right;
        const spaceOnLeft = buttonRect.left;
        const spaceOnBottom = windowHeight - buttonRect.bottom;
        const spaceOnTop = buttonRect.top;

        // Horizontal positioning (a full-width menu has no side to pick)
        if (matchTriggerWidth) {
          setMenuPosition("left-0 right-0");
        } else if (spaceOnRight < menuWidth && spaceOnLeft >= menuWidth) {
          setMenuPosition("right-0");
        } else {
          setMenuPosition("left-0");
        }

        // Vertical positioning - prefer bottom, but use top if not enough space
        if (spaceOnBottom < menuHeight && spaceOnTop >= menuHeight) {
          setMenuPlacement("top");
        } else {
          setMenuPlacement("bottom");
        }
      }
    }

    setIsDropdownOpen((prev) => !prev);
  };

  fullWidth = fullWidth ? "w-full" : "inline-block";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Determine what text to show on the button.
  // In singleSelect mode, if one item is selected, display that option's name.
  const selectedDisplay = singleSelect
    ? selected.length === 1
      ? options.find((opt) => opt.id === selected[0])?.name || "Select"
      : "Select"
    : selected.length > 0
    ? selected.length + " selected"
    : "All";

  return (
    <div className={`relative text-left ${fullWidth}`} ref={dropdownRef}>
      {/* Render label above if labelPosition is "outset" */}
      {labelPosition === "outset" && (
        <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}

      <button
        className={`inline-flex justify-between gap-x-1.5 w-full rounded-md px-3 ${matchTriggerWidth ? "" : "-ml-1"} py-2 items-center text-sm leading-none font-normal text-gray-900 dark:text-gray-200 border-0 transition-colors duration-150 ${
          disabled
            ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
            : isDropdownOpen
            ? "bg-gray-50 dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-white/[0.06]"
            : "bg-gray-50 dark:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/[0.06]"
        }`}
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <div className="inline-flex w-full items-center gap-x-1.5">
          {/* Render label inside if labelPosition is "inset" */}
          {labelPosition === "inset" && (
            <span className="text-gray-500 dark:text-gray-300">{label}:</span>
          )}
          <span className="text-gray-600 dark:text-gray-200 whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
            {selectedDisplay}
          </span>
        </div>
        <ChevronDownIcon
          className={`-mr-1 h-5 w-5 ${
            disabled ? "text-gray-300 dark:text-gray-500" : "text-gray-500 dark:text-gray-400"
          }`}
          aria-hidden="true"
        />
      </button>

      <Transition
        show={isDropdownOpen}
        enter="transition ease-out duration-100 transform"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75 transform"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
        className={`absolute ${menuPosition} ${
          menuPlacement === "top" ? "bottom-full mb-2" : "top-full mt-2"
        } z-50 ${matchTriggerWidth ? "" : "w-56"} origin-top-right divide-y divide-gray-100 dark:divide-gray-700 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none`}
      >
        <div ref={listRef} className="p-4 max-h-60 overflow-y-auto">
          {isLoading ? (
            <p className="text-gray-600 dark:text-gray-300 text-sm">Loading...</p>
          ) : options.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              No {label.toLowerCase()} found
            </p>
          ) : (
            <>
              <div className="pb-2 flex items-center justify-between">
                <p className="text-xsm uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {label}
                </p>
                {onClear && (
                  <button
                    className="text-sm text-brand-primary hover:underline"
                    onClick={onClear}
                  >
                    Clear
                  </button>
                )}
              </div>
              {options.map((option) => (
                <div
                  key={option.id}
                  data-selected={selected.includes(option.id)}
                  className={`pl-2 py-2 -ml-2 -mr-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer flex items-center justify-between hover:bg-gray-50 dark:hover:bg-white/[0.06] rounded-md ${
                    (optionStatus && !option.is_active) || option.disabled
                      ? "opacity-50 cursor-not-allowed hover:bg-white dark:hover:bg-gray-800"
                      : ""
                  }`}
                  onClick={() => {
                    if (option.disabled) return;
                    if (!optionStatus || (optionStatus && option.is_active)) {
                      if (singleSelect) {
                        // In single select mode, replace any existing selection with this one.
                        onUpdate([option.id]);
                        setIsDropdownOpen(false);
                      } else {
                        // For multi-select mode, let onUpdate handle toggling the selection.
                        onUpdate(option.id);
                      }
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    {optionStatus && (
                      <span
                        className={`mr-2 h-2 w-2 rounded-full inline ${
                          option.is_active ? "bg-success" : "bg-danger"
                        }`}
                      />
                    )}
                    {option.name}
                  </div>
                  <div className="mr-2 h-5 w-5">
                    {selected.includes(option.id) &&
                    (!optionStatus || option.is_active) ? (
                      <CheckIcon className="text-brand-primary" />
                    ) : null}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </Transition>
    </div>
  );
}

export { MultiSelect };
